import { Box, Button, FormControl, Grid, Switch as ChakraSwitch } from '@chakra-ui/react'
import { UseFieldArrayRemove, useFieldArray, useFormContext } from 'react-hook-form'

import { ConfirmRemoveButton } from '../../components/ConfirmRemoveButton'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import { InputGroup } from '../../components/InputGroup'
import { SwitchControl } from '../../components/SwitchControl'
import { Switch } from '../../components/Switch'
import { Add } from '../../icons'
import { Datepicker } from '../../components/Datepicker'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { useState } from 'react'

export const defaultInsuree = {
  firstName: '',
  lastName: '',
  birthdate: ''
}

type AdditionalInsureesProps = {
  prefix: string
  editableBirthdate?: boolean
  editableBirthnumber?: boolean
}

type AdditionalInsureeRowProps = {
  prefix: string
  index: number
  contracted: boolean
  remove: UseFieldArrayRemove
  editableBirthdate?: boolean
  editableBirthnumber?: boolean
}

export const AdditionalInsurees = ({
  prefix,
  editableBirthdate = false,
  editableBirthnumber = false
}: AdditionalInsureesProps) => {
  const { control } = useFormContext()
  const { insuranceParams } = useInsuranceParamsContext()
  const contracted = !!insuranceParams?.generalParams.contractNumber
  const { fields, remove, append } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: prefix, // unique name for your Field Array
  })

  return (
    <Box sx={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
      {fields.map((field, index) => {
        return (
          <AdditionalInsureeRow prefix={prefix}
            index={index} contracted={contracted}
            editableBirthdate={editableBirthdate}
            editableBirthnumber={editableBirthnumber}
            remove={remove}
            key={field.id} />
        )
      })}

      {/* zakomentováno - rodinná sleva je pouze na opakované roční cesty, navíc pojem rodiny si pojišťovny vykládají různě
      {fields.length > 1 && !editableBirthnumber &&
        <FormControl>
          <SwitchControl>
            <ChakraSwitch
              //checked={contracted}
              // defaultChecked={contracted}
              {...register('travel.areInsureesInFamily')}
              isDisabled={contracted}
            //onChange={(e) => setIsPolicyHolderInsureed(e.target.checked)}
            />
            <FormLabel variant="switch" tooltip='Pojišťovny mohou poskytnout slevu, pokud pojištěnými jsou rodiče a jejich děti.'>
              Jsou pojištěné osoby členy jedné rodiny
            </FormLabel>
          </SwitchControl>
        </FormControl>
      } */}

      {!editableBirthnumber && !contracted &&
        <Button type="button" variant="outline" onClick={() => append(defaultInsuree)}>
          <Add color="secondary.500" />
          Přidat další osobu
        </Button>
      }
    </Box>
  )
}


export const AdditionalInsureeRow = ({
  prefix,
  index,
  contracted,
  remove,
  editableBirthdate = false,
  editableBirthnumber = false
}: AdditionalInsureeRowProps) => {
  const { getValues, register, setValue, watch, getFieldState } = useFormContext()

  const [rc, setRc] = useState(getValues(`${prefix}.${index}.birthnumber`))

  const sanitizeRc = (value: string) => {
    return value.replace(/\D/g, '');
  }

  const isPh = watch(`${prefix}.${index}.isPolicyHolder`)
  const isForeigner = watch(`${prefix}.${index}.isForeigner`)

  const firstNameError = getFieldState(`${prefix}.${index}.firstName`).error
  const lastNameError = getFieldState(`${prefix}.${index}.lastName`).error
  const birthnumberError = getFieldState(`${prefix}.${index}.birthnumber`).error
  const birthdateError = getFieldState(`${prefix}.${index}.birthdate`).error

  return (
    <Grid
      sx={{
        gap: 4,
        p: 4,
        justifyItems: 'start',
        bg: 'gray.100',
        borderRadius: 'md',
      }}
    >
      <FormGroup sx={{ columnGap: 4 }}>
        <FormControl isRequired={editableBirthnumber && !isPh} isInvalid={!!firstNameError}>
          <FormLabel>Jméno</FormLabel>

          <InputGroup
            htmlSize={12}
            placeholder="Jan"
            isDisabled={!!isPh || contracted}
            errors={firstNameError}
            {...register(`${prefix}.${index}.firstName`)}
          />
        </FormControl>

        <FormControl isRequired={editableBirthnumber && !isPh} isInvalid={!!lastNameError}>
          <FormLabel>Příjmení</FormLabel>

          <InputGroup
            htmlSize={12}
            placeholder="Novák"
            isDisabled={!!isPh || contracted}
            errors={lastNameError}
            {...register(`${prefix}.${index}.lastName`)}
          />
        </FormControl>

        {editableBirthnumber &&
          <FormControl /*isRequired={!isPh}*/ isInvalid={!!birthnumberError}>
            <FormLabel>Rodné číslo</FormLabel>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SwitchControl>
                <Switch {...register(`${prefix}.${index}.isForeigner`)} isDisabled={!!isPh || contracted} />
                <FormLabel htmlFor={`${prefix}.${index}.isForeigner`} variant="switch">
                  Nemá
                </FormLabel>
              </SwitchControl>
              {!isForeigner &&
                <InputGroup
                  htmlSize={12}
                  isDisabled={!!isPh || contracted}
                  errors={birthnumberError}
                  value={rc}
                  {...register(`${prefix}.${index}.birthnumber`, {
                    setValueAs: v => sanitizeRc(v),
                    onChange: (e) => setRc(sanitizeRc(e.target.value).toString())
                  })}
                />
              }
            </Box>
          </FormControl>
        }

        <FormControl isRequired={editableBirthdate && !isPh} isInvalid={!!birthdateError}>
          <FormLabel htmlFor={`${prefix}.${index}.birthdate`}>Datum narození</FormLabel>

          <Datepicker
            isDisabled={!editableBirthdate || !!isPh || contracted}
            errors={birthdateError}
            date={getValues(`${prefix}.${index}.birthdate`)}
            onDateChange={(date) => setValue(`${prefix}.${index}.birthdate`, date)}
          />
        </FormControl>

        {!editableBirthnumber && !contracted &&
          <Grid sx={{ h: '100%', alignItems: 'end' }}>
            <ConfirmRemoveButton
              hideCancelButton
              fullWidth={false}
              onClick={() => remove(index)}
            />
          </Grid>
        }
      </FormGroup>
    </Grid>
  )
}
